/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';
import { useMessageSeverity } from '../hooks/PRIVATE_useMessageSeverity';
import { merge } from '../utils/UNSAFE_stringUtils';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { InlineUserAssistance } from '../UNSAFE_UserAssistance';

type UseCheckboxRadioFieldProps = {
  ariaDescribedBy?: string;
  baseId: string;
  hasValue?: boolean;
  hasTopUserAssistance?: boolean;
  hasBottomUserAssistance?: boolean;
  isFocused?: boolean; // TODO: check if this is needed
  isReadonly?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  label: string;
  labelEdge?: 'none' | 'top' | 'start' | 'inside';
  styleVariant?: 'default' | 'embedded'; // TODO: check if this is needed
  messages?: ComponentProps<typeof InlineUserAssistance>['messages'];
  parentComponentVariant: 'radioSet' | 'checkboxSet';
};

type GroupProps = {
  groupProps: {
    'aria-describedby'?: HTMLAttributesSignalExcluded['aria-describedby'];
    'aria-labelledby'?: HTMLAttributesSignalExcluded['aria-labelledby'];
    'aria-label'?: HTMLAttributesSignalExcluded['aria-label'];
    'aria-required'?: HTMLAttributesSignalExcluded['aria-required'];
    'aria-invalid'?: HTMLAttributesSignalExcluded['aria-invalid'];
    'aria-readonly'?: HTMLAttributesSignalExcluded['aria-readonly'];
    'aria-disabled'?: HTMLAttributesSignalExcluded['aria-disabled'];
    id: HTMLAttributesSignalExcluded['id'];
  };
  labelProps: {
    forId: string;
    id: string;
    isRequiredShown?: boolean;
  };
  topUserAssistanceProps?: {
    id: string;
    variant: 'reflow';
  };
  bottomUserAssistanceProps?: {
    id: string;
    isRequiredShown: boolean;
  };
  readonlyId?: string;
};

export function useCheckboxRadioField({
  ariaDescribedBy,
  baseId,
  hasValue,
  hasTopUserAssistance,
  hasBottomUserAssistance,
  isReadonly,
  isRequired = false,
  isDisabled,
  label,
  labelEdge,
  messages,
  parentComponentVariant
}: UseCheckboxRadioFieldProps): GroupProps {
  const optionsGroupId = `${baseId}-group`;
  const labelId = `${baseId}-label`;
  const topUaId = hasTopUserAssistance ? `${baseId}-top-ua` : undefined;
  const bottomUaId = hasBottomUserAssistance ? `${baseId}-bottom-ua` : undefined;
  const readonlyId = isReadonly ? `${baseId}-readonly` : undefined;
  const ariaLabel = label && label.length > 0 && labelEdge === 'none' ? label : undefined;
  const ariaLabelledBy = ariaLabel ? undefined : labelId;
  const isRadioSet = parentComponentVariant === 'radioSet';
  const highestMessageSeverity = useMessageSeverity(messages);
  const hasErrorMessage = highestMessageSeverity === 'error';

  return {
    groupProps: {
      'aria-describedby': merge([topUaId, bottomUaId, readonlyId, ariaDescribedBy]),
      ...(isRadioSet && { 'aria-required': isRequired }),
      ...(ariaLabelledBy && { 'aria-labelledby': ariaLabelledBy }),
      ...(ariaLabel && { 'aria-label': ariaLabel }),
      ...(isReadonly && isRadioSet && { 'aria-readonly': true }),
      ...(hasErrorMessage && { 'aria-invalid': true }),
      ...(isDisabled && { 'aria-disabled': true }),
      id: optionsGroupId
    },
    labelProps: {
      forId: optionsGroupId,
      id: labelId
    },
    // TOP UA is assistive text and help link/text
    ...(hasTopUserAssistance && {
      topUserAssistanceProps: {
        id: topUaId as string,
        // Use reflow variant so that top UA doesn't have min height
        variant: 'reflow'
      }
    }),
    // Bottom UA is required indicator or messages
    ...(hasBottomUserAssistance && {
      bottomUserAssistanceProps: {
        id: bottomUaId as string,
        isRequiredShown: !!isRequired && !hasValue
      }
    }),
    ...(isReadonly && {
      readonlyId
    })
  };
}
